import type { AreaPageContent } from "@site/lib/cms/areaPageTypes";
import { useGlobalPhone } from "@site/contexts/SiteSettingsContext";
import { useHubPageLocations } from "@site/hooks/useHubPageLocations";
import AboutHero from "@site/components/about/AboutHero";
import LocationsGrid from "@site/components/areas-we-serve/LocationsGrid";
import PracticeAreaDetailFaq from "@site/components/practice-detail/PracticeAreaDetailFaq";

function HeadingRenderer({ text, level }: { text: string; level: 1 | 2 | 3 | 4 }) {
  const classes: Record<number, string> = {
    1: "font-playfair text-[36px] md:text-[54px] lg:text-[64px] leading-tight md:leading-[64px] text-black pb-[20px] md:pb-[30px]",
    2: "font-playfair text-[32px] md:text-[42px] lg:text-[48px] leading-tight md:leading-[48px] text-black pb-[20px] md:pb-[30px]",
    3: "font-playfair text-[28px] md:text-[36px] lg:text-[42px] leading-tight md:leading-[42px] text-black pb-[20px] md:pb-[30px]",
    4: "font-playfair text-[24px] md:text-[32px] lg:text-[36px] leading-tight md:leading-[36px] text-black pb-[20px] md:pb-[30px]",
  };
  const Tag = `h${level}` as keyof JSX.IntrinsicElements;
  return <Tag className={classes[level]}>{text}</Tag>;
}

function FullWidthImage({ src, alt }: { src?: string; alt?: string }) {
  if (!src) return null;
  return (
    <div className="w-full">
      <img
        src={src}
        alt={alt || ""}
        className="w-full h-auto object-cover"
        loading="lazy"
      />
    </div>
  );
}

function BodyContent({ html }: { html: string }) {
  return (
    <div
      className={[
        "prose prose-neutral max-w-none pb-[30px]",
        // Font overrides
        "prose-p:font-outfit prose-p:text-[16px] md:prose-p:text-[18px] prose-p:leading-[1.7] prose-p:text-black prose-p:my-4",
        // Heading overrides
        "prose-headings:font-playfair prose-headings:text-black prose-headings:mt-6 prose-headings:mb-3",
        // Link overrides
        "prose-a:text-law-accent prose-a:underline prose-a:font-normal",
        // List overrides
        "prose-li:font-outfit prose-li:text-[16px] prose-li:text-black",
        // Ensure images are responsive
        "prose-img:rounded prose-img:my-4",
      ].join(" ")}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

interface AreaPageRendererProps {
  content: AreaPageContent;
  lastUpdated?: string;
}

export default function AreaPageRenderer({
  content,
  lastUpdated,
}: AreaPageRendererProps) {
  const { phoneDisplay } = useGlobalPhone();
  const { locationsSection: hubLocations, cta: hubCta } = useHubPageLocations();
  // Use hub page data as source of truth; fall back to page's own data
  const effectiveLocations = hubLocations ?? content.locationsSection;
  const effectiveCta = hubCta ?? content.cta;

  return (
    <>
      {/* Hero */}
      <AboutHero
        content={content.hero}
        phoneDisplay={phoneDisplay}
        phoneLabel="Call us"
        lastUpdated={lastUpdated}
      />

      {/* Main Content Section */}
      <div className="bg-white pt-[30px] md:pt-[54px] pb-[30px] md:pb-[54px]">
        <div className="max-w-[2560px] mx-auto w-[95%] md:w-[90%] lg:w-[80%]">
          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-[4%]">
            {/* Left Column */}
            <div className="lg:col-span-3 space-y-8">
              {/* Intro Section */}
              <div>
                <HeadingRenderer text={content.introSection.heading} level={content.introSection.headingLevel} />
                <BodyContent html={content.introSection.body} />
              </div>

              {/* Intro Section Image (full-width) */}
              {content.introSection.image && (
                <FullWidthImage src={content.introSection.image} alt={content.introSection.imageAlt} />
              )}

              {/* Why Section — only render when there is actual body content */}
              {content.whySection.body?.trim() && (
                <>
                  <div className="pt-[20px]">
                    {content.whySection.heading && (
                      <HeadingRenderer text={content.whySection.heading} level={content.whySection.headingLevel} />
                    )}
                    <BodyContent html={content.whySection.body} />
                  </div>
                  {content.whySection.image && (
                    <FullWidthImage src={content.whySection.image} alt={content.whySection.imageAlt} />
                  )}
                </>
              )}

              {/* Closing Section — only render when there is actual body content */}
              {content.closingSection.body?.trim() && (
                <>
                  <div className="pt-[20px]">
                    {content.closingSection.heading && (
                      <HeadingRenderer text={content.closingSection.heading} level={content.closingSection.headingLevel} />
                    )}
                    <BodyContent html={content.closingSection.body} />
                  </div>
                  {content.closingSection.image && (
                    <FullWidthImage src={content.closingSection.image} alt={content.closingSection.imageAlt} />
                  )}
                </>
              )}
            </div>

            {/* Right Column — Sticky CTA Card */}
            <div className="lg:col-span-2">
              <div className="lg:sticky lg:top-[100px]">
                {effectiveCta.image && (
                  <div className="relative overflow-hidden mb-0">
                    <img
                      src={effectiveCta.image}
                      alt={effectiveCta.imageAlt}
                      className="w-full h-auto object-cover"
                      loading="lazy"
                    />
                    <div className="absolute bottom-0 left-0 right-0 h-[60px] bg-gradient-to-t from-[#1a1a2e] to-transparent pointer-events-none" />
                  </div>
                )}
                <div className="bg-[#1a1a2e] p-6 text-center space-y-4">
                  <h3 className="font-playfair text-[22px] md:text-[26px] text-white leading-tight">
                    {effectiveCta.heading}
                  </h3>
                  <p className="font-outfit text-[15px] md:text-[16px] leading-[24px] text-white/80">
                    {effectiveCta.description}
                  </p>
                  <a
                    href={effectiveCta.secondaryButton.href}
                    className="block w-full bg-law-accent text-black font-outfit text-[16px] md:text-[18px] font-semibold py-3 px-6 hover:bg-[#7a9e10] transition-colors duration-300 text-center"
                  >
                    <div>{effectiveCta.secondaryButton.label}</div>
                    <div className="text-xs font-normal">{effectiveCta.secondaryButton.sublabel}</div>
                  </a>
                  <a
                    href={`tel:${phoneDisplay.replace(/[^0-9]/g, "")}`}
                    className="flex items-center justify-center gap-2 text-white hover:text-law-accent transition-colors duration-300"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 00-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z" />
                    </svg>
                    <span className="font-outfit text-[18px] md:text-[20px]">
                      {phoneDisplay}
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section — Full Width, below Closing Section */}
      {content.faq?.enabled && content.faq.items.length > 0 && (
        <PracticeAreaDetailFaq
          content={{
            enabled: content.faq.enabled,
            heading: content.faq.heading,
            description: undefined,
            items: content.faq.items,
          }}
        />
      )}

      {/* Locations Section — Full Width (sourced from hub page /areas-we-serve/) */}
      {effectiveLocations.items.length > 0 && (
        <div className="bg-gray-50 py-[40px] md:py-[60px]">
          <div className="max-w-[2560px] mx-auto w-[95%] md:w-[90%] lg:w-[80%]">
            <h3 className="font-playfair text-[28px] md:text-[36px] lg:text-[42px] leading-tight text-black pb-[20px]">
              {effectiveLocations.heading}
            </h3>
            {effectiveLocations.introText && (
              <p className="font-outfit text-[16px] md:text-[18px] leading-[24px] md:leading-[28px] text-black pb-[30px]">
                {effectiveLocations.introText}
              </p>
            )}
            <LocationsGrid items={effectiveLocations.items} />
          </div>
        </div>
      )}

      {/* Map Section — Full Width */}
      {content.mapSection.embedUrl && (
        <div className="bg-white py-[40px] md:py-[60px]">
          <div className="max-w-[2560px] mx-auto w-[95%] md:w-[90%] lg:w-[80%]">
            {content.mapSection.heading && (
              <h3 className="font-playfair text-[28px] md:text-[36px] lg:text-[42px] leading-tight text-black pb-[20px]">
                {content.mapSection.heading}
              </h3>
            )}
            <div className="w-full overflow-hidden rounded-lg">
              <iframe
                src={content.mapSection.embedUrl}
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={content.mapSection.heading || "Service area map"}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
